/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });

  return cartData;
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

const DCW_SERVICE_FEE_VARIANT_ID = 53609445261648;
const DCW_KITCHEN_CHEF_FALLBACK_PRODUCT_IDS = new Set([
  '10575612051792',
  '10575619653968',
  '10575737356624',
  '10575619293520',
  '10575615230288',
]);
const DCW_KITCHEN_CHEF_COLLECTION_HANDLE = 'kuchnia-szefa';
const DCW_KITCHEN_CHEF_CACHE_TTL = 5 * 60 * 1000;
let dcwServiceFeeSyncPromise;
let dcwServiceFeeSyncTimer;
let dcwServiceFeeCheckoutReady = false;
let dcwKitchenChefProductIds;
let dcwKitchenChefProductsFetchedAt = 0;

async function fetchDcwCart() {
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

async function fetchDcwKitchenChefProductIds() {
  const now = Date.now();
  if (
    dcwKitchenChefProductIds &&
    now - dcwKitchenChefProductsFetchedAt < DCW_KITCHEN_CHEF_CACHE_TTL
  ) {
    return dcwKitchenChefProductIds;
  }

  try {
    const shopUrl = window.shopUrl || window.location.origin;
    const response = await fetch(
      `${shopUrl}/collections/${DCW_KITCHEN_CHEF_COLLECTION_HANDLE}/products.json?limit=250`,
      { headers: { Accept: 'application/json' } }
    );
    if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
    const payload = await response.json();
    const productIds = new Set(
      (payload.products || [])
        .map((product) => String(product.id || ''))
        .filter(Boolean)
    );
    dcwKitchenChefProductIds = productIds.size
      ? productIds
      : new Set(DCW_KITCHEN_CHEF_FALLBACK_PRODUCT_IDS);
  } catch (error) {
    console.warn('[DCW] Kitchen Chef collection lookup failed; using the last known products.', error);
    dcwKitchenChefProductIds = new Set(DCW_KITCHEN_CHEF_FALLBACK_PRODUCT_IDS);
  }

  dcwKitchenChefProductsFetchedAt = now;
  return dcwKitchenChefProductIds;
}

function isDcwKitchenChefItem(item, kitchenChefProductIds) {
  return kitchenChefProductIds.has(String(item.product_id || ''));
}

function updateDcwServiceFeeNoticeVisibility(cart, kitchenChefProductIds) {
  const hasChargeableProducts = (cart?.items || []).some((item) => {
    const isServiceFee = Number(item.variant_id) === DCW_SERVICE_FEE_VARIANT_ID;
    return !isServiceFee && !isDcwKitchenChefItem(item, kitchenChefProductIds);
  });

  document.querySelectorAll('[data-dcw-service-fee-notice]').forEach((notice) => {
    notice.hidden = !hasChargeableProducts;
  });
}

async function changeDcwCartLine(key, quantity) {
  const changeUrl = (typeof routes !== 'undefined' && routes?.cart_change_url) || '/cart/change';
  const response = await fetch(`${changeUrl}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: key, quantity }),
  });
  if (!response.ok) throw new Error(`Cart change failed: ${response.status}`);
  return response.json();
}

async function addDcwServiceFee() {
  const addUrl = (typeof routes !== 'undefined' && routes?.cart_add_url) || '/cart/add';
  const response = await fetch(`${addUrl}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: [
        {
          id: DCW_SERVICE_FEE_VARIANT_ID,
          quantity: 1,
          properties: { _dcw_service_fee: 'true' },
        },
      ],
    }),
  });
  if (!response.ok) throw new Error(`Service fee add failed: ${response.status}`);
  return response.json();
}

function reconcileDcwServiceFee() {
  if (dcwServiceFeeSyncPromise) return dcwServiceFeeSyncPromise;

  dcwServiceFeeSyncPromise = (async () => {
    const [cart, kitchenChefProductIds] = await Promise.all([
      fetchDcwCart(),
      fetchDcwKitchenChefProductIds(),
    ]);
    const feeLines = cart.items.filter((item) => Number(item.variant_id) === DCW_SERVICE_FEE_VARIANT_ID);
    const hasChargeableProducts = cart.items.some((item) => {
      const isServiceFee = Number(item.variant_id) === DCW_SERVICE_FEE_VARIANT_ID;
      return !isServiceFee && !isDcwKitchenChefItem(item, kitchenChefProductIds);
    });
    updateDcwServiceFeeNoticeVisibility(cart, kitchenChefProductIds);
    let changed = false;

    if (hasChargeableProducts && feeLines.length === 0) {
      await addDcwServiceFee();
      changed = true;
    }

    if (hasChargeableProducts && feeLines.length > 0) {
      if (feeLines[0].quantity !== 1) {
        await changeDcwCartLine(feeLines[0].key, 1);
        changed = true;
      }
      for (const extraLine of feeLines.slice(1)) {
        await changeDcwCartLine(extraLine.key, 0);
        changed = true;
      }
    }

    if (!hasChargeableProducts) {
      for (const feeLine of feeLines) {
        await changeDcwCartLine(feeLine.key, 0);
        changed = true;
      }
    }

    if (changed) {
      const refreshedCart = await refreshDawnCartUI();
      updateDcwServiceFeeNoticeVisibility(refreshedCart || cart, kitchenChefProductIds);
    }
    return true;
  })()
    .catch((error) => {
      console.error('[DCW] Service fee synchronization failed.', error);
      return false;
    })
    .finally(() => {
      dcwServiceFeeSyncPromise = undefined;
    });

  return dcwServiceFeeSyncPromise;
}

function scheduleDcwServiceFeeSync() {
  window.clearTimeout(dcwServiceFeeSyncTimer);
  dcwServiceFeeSyncTimer = window.setTimeout(reconcileDcwServiceFee, 0);
}

document.addEventListener(
  'click',
  (event) => {
    const checkoutButton = event.target.closest('button[name="checkout"], input[name="checkout"]');
    if (!checkoutButton) return;
    if (dcwServiceFeeCheckoutReady) {
      dcwServiceFeeCheckoutReady = false;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    reconcileDcwServiceFee().then((ready) => {
      if (!ready) return;
      dcwServiceFeeCheckoutReady = true;
      if (checkoutButton.form) {
        checkoutButton.form.requestSubmit(checkoutButton);
      } else {
        checkoutButton.click();
      }
    });
  },
  true
);

document.addEventListener(
  'DOMContentLoaded',
  () => {
    reconcileDcwServiceFee();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleDcwServiceFeeSync);
    }
  },
  { once: true }
);

window.addEventListener('pageshow', scheduleDcwServiceFeeSync);
